/**
 * GPC Smoke Test Module
 * 
 * This module provides a simple smoke test function to verify that GPC (Global Privacy Control)
 * is working correctly in the CMP. It checks:
 * 1. Whether GPC is enabled in the browser
 * 2. Whether consent cookies are present and have expected values
 * 3. Whether the CMP signal status is "ready" after GPC application
 * 
 * Usage in DevTools Console:
 * 
 *   // Import and run the smoke test
 *   import { runGpcSmokeTest } from './smokeTests/gpcSmokeTest';
 *   runGpcSmokeTest();
 * 
 * Or if exposed globally:
 *   window.__gpcSmokeTest();
 */

import { isGpcEnabled } from '../gpc';
import { getCookie } from '../util';

/**
 * Result of a GPC smoke test check
 */
interface SmokeTestResult {
  passed: boolean;
  message: string;
  details?: any;
}

/**
 * Checks if GPC is enabled in the browser
 */
function checkGpcEnabled(): SmokeTestResult {
  const gpcEnabled = isGpcEnabled();
  
  return {
    passed: true, // This check always passes, it's informational
    message: `GPC ${gpcEnabled ? 'IS' : 'IS NOT'} enabled in browser`,
    details: {
      gpcEnabled,
      navigatorGpc: typeof navigator !== 'undefined' ? (navigator as any).globalPrivacyControl : undefined
    }
  };
}

/**
 * Checks if the GPP consent cookie is present and has content
 */
function checkGppCookie(): SmokeTestResult {
  const cookieName = 'amp-gpp-consent-string';
  const cookieValue = getCookie(cookieName);
  
  const hasCookie = !!cookieValue;
  const cookieLength = cookieValue ? cookieValue.length : 0;
  
  return {
    passed: hasCookie,
    message: hasCookie 
      ? `✓ GPP consent cookie present (${cookieLength} chars)`
      : `✗ GPP consent cookie NOT found`,
    details: {
      cookieName,
      cookieLength,
      cookieValue: cookieValue ? `${cookieValue.substring(0, 50)}...` : null
    }
  };
}

/**
 * Checks if the GDPR TCF cookie is present (when GDPR applies)
 */
function checkTcfCookie(): SmokeTestResult {
  const cookieName = 'euconsent-v2';
  const cookieValue = getCookie(cookieName);
  
  const hasCookie = !!cookieValue;
  const cookieLength = cookieValue ? cookieValue.length : 0;
  
  // This check is informational - cookie may not exist if GDPR doesn't apply
  return {
    passed: true,
    message: hasCookie 
      ? `✓ TCF consent cookie present (${cookieLength} chars)`
      : `ℹ TCF consent cookie not found (may not apply)`,
    details: {
      cookieName,
      cookieLength,
      cookieValue: cookieValue ? `${cookieValue.substring(0, 50)}...` : null
    }
  };
}

/**
 * Checks if the CMP signal status is "ready"
 */
function checkCmpSignalStatus(): SmokeTestResult {
  if (typeof window === 'undefined' || !window.cmpApi) {
    return {
      passed: false,
      message: '✗ CMP API not available',
      details: { cmpApi: null }
    };
  }
  
  try {
    // Try to get signal status via ping
    let signalStatus: string | null = null;
    
    if (typeof window.__gpp === 'function') {
      window.__gpp('ping', (pingData: any) => {
        signalStatus = pingData?.signalStatus;
      });
    }
    
    const isReady = signalStatus === 'ready';
    
    return {
      passed: isReady,
      message: isReady 
        ? '✓ CMP signal status is "ready"'
        : `✗ CMP signal status is "${signalStatus}" (expected "ready")`,
      details: { signalStatus }
    };
  } catch (error) {
    return {
      passed: false,
      message: '✗ Error checking CMP signal status',
      details: { error: String(error) }
    };
  }
}

/**
 * Checks if GPC was applied to US state sections (if applicable)
 */
function checkGpcAppliedToUsSections(): SmokeTestResult {
  if (typeof window === 'undefined' || !window.cmpApi) {
    return {
      passed: false,
      message: '✗ CMP API not available',
      details: null
    };
  }
  
  try {
    const cmpApi = window.cmpApi;
    const applicableSections = cmpApi.getApplicableSections ? cmpApi.getApplicableSections() : [];
    
    // Check if any US state sections are present
    const usStateSections = [8, 9, 10, 11, 12]; // CA, VA, CO, UT, CT
    const hasUsSection = applicableSections.some((id: number) => usStateSections.includes(id));
    
    if (!hasUsSection) {
      return {
        passed: true,
        message: 'ℹ No US state sections applicable',
        details: { applicableSections }
      };
    }
    
    // Try to check if opt-out flags are set
    const gppString = cmpApi.getGppString ? cmpApi.getGppString() : '';
    
    return {
      passed: true,
      message: `✓ US state sections present (${applicableSections.join(', ')})`,
      details: {
        applicableSections,
        gppStringLength: gppString.length
      }
    };
  } catch (error) {
    return {
      passed: false,
      message: '✗ Error checking US sections',
      details: { error: String(error) }
    };
  }
}

/**
 * Runs all GPC smoke tests and returns a summary
 */
export function runGpcSmokeTest(): void {
  console.log('\n========================================');
  console.log('GPC SMOKE TEST');
  console.log('========================================\n');
  
  const tests: Array<{ name: string; fn: () => SmokeTestResult }> = [
    { name: 'GPC Browser Detection', fn: checkGpcEnabled },
    { name: 'GPP Consent Cookie', fn: checkGppCookie },
    { name: 'TCF Consent Cookie', fn: checkTcfCookie },
    { name: 'CMP Signal Status', fn: checkCmpSignalStatus },
    { name: 'US State Sections', fn: checkGpcAppliedToUsSections }
  ];
  
  const results: SmokeTestResult[] = [];
  
  tests.forEach(({ name, fn }) => {
    console.log(`\n[${name}]`);
    const result = fn();
    results.push(result);
    console.log(result.message);
    if (result.details) {
      console.log('Details:', result.details);
    }
  });
  
  console.log('\n========================================');
  const passedCount = results.filter(r => r.passed).length;
  const totalCount = results.length;
  console.log(`SUMMARY: ${passedCount}/${totalCount} checks passed`);
  console.log('========================================\n');
}

/**
 * Expose smoke test to window for easy DevTools access
 */
if (typeof window !== 'undefined') {
  (window as any).__gpcSmokeTest = runGpcSmokeTest;
}
